// src/pages/CommunicationPage.js
import { DashboardControllerHtml } from "../../components/skins/dashboard-controller.ts";
import { globalState } from "../../framework/globalState.ts";
import { subscribeRegister } from "../../services/mqttClient.ts";
import { FullscreenToggle } from "../../components/utils/fullscreen-toggle.ts";
import { groupByDictionaryKey } from '../../services/registerUtils.ts';

export default function CommunicationPage() {
  console.log("[CommunicationPage] Initializing...");

  const container = document.createElement("div");
  container.classList.add("parent-container");

  try {
    container.innerHTML = new DashboardControllerHtml().contrllerskin().trim();
    console.log("[CommunicationPage] Skin loaded");
  } catch (error) {
    console.error("[CommunicationPage] Skin error:", error);
    container.innerHTML = "<p>Error loading dashboard skin</p>";
    return container;
  }

  const siteWithData = globalState.currentSiteWithData;
  const site = siteWithData?.site;
  const registers = (siteWithData?.devices || []).flatMap(device =>
    (device.registers || []).map(register => ({
      ...register,
      deviceId: device._id,
      deviceName: device.name,
    }))
  );

  const subscriptions = [];

  if (!site?._id || !registers.length) {
    console.warn("[CommunicationPage] Site or registers not ready");
    container.innerHTML += "<p class='text-center text-warning'>Site not selected or no registers.</p>";
    return container;
  }

  console.log(`[CommunicationPage] Site: ${site._id} (${site.name || 'Unnamed'})`);


  // === CONFIG KEYS ===
  const configKeys = {
    inverter: "comm_inverter_active",
    bess: "comm_bess_soc",
    generator: "comm_diesel_active",
    solar: "comm_pv_plant_active",
    mv: "comm_mv_active"
  };

  const groups = groupByDictionaryKey(registers);

  // === SECTION SELECTORS ===
  const sections = {
    circuit: container.querySelector(".container-column .column-header h3").textContent === "Circuit"
      ? container.querySelector(".container-column:nth-child(1) table")
      : null,
    ppc: container.querySelector(".container-column .column-header h3").textContent === "PPC"
      ? container.querySelector(".container-column:nth-child(2) table")
      : null,
    solar: container.querySelector(".container-column .column-header h3").textContent === "Substation Solar"
      ? container.querySelector(".container-column:nth-child(3) table")
      : null,
    bess: container.querySelector(".container-column .column-header h3").textContent === "Substation BESS"
      ? container.querySelector(".container-column:nth-child(4) table")
      : null,
    generator: container.querySelector(".container-column .column-header h3").textContent === "Substation Generator"
      ? container.querySelector(".container-column:nth-child(5) table")
      : null
  };

  // === LABEL → TYPE MAPPING (from skin) ===
  const labelToType = {};
  const typeToTable = {
    inverter: sections.circuit,
    mv: sections.circuit,
    generator: sections.generator,
    solar: sections.solar,
    bess: sections.bess
  };

  // Build label map from existing rows
  Object.entries(typeToTable).forEach(([type, table]) => {
    if (!table) return;
    const rows = table.querySelectorAll("tbody tr");
    rows.forEach(row => {
      const labelCell = row.querySelector("td:first-child");
      if (labelCell) {
        const label = labelCell.textContent.trim();
        labelToType[label] = type;
      }
    });
  });

  // === LAST SEEN TRACKING ===
  const lastSeen = new Map(); // registerName → timestamp

  function formatLastSeen(ms) {
    if (!ms) return "Never";
    const sec = Math.floor((Date.now() - ms) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  }

  function updateLastSeen(cell, regName) {
    const now = Date.now();
    lastSeen.set(regName, now);
    let span = cell.querySelector(".last-seen");
    if (!span) {
      span = document.createElement("span");
      span.className = "last-seen text-xs opacity-70 ml-2";
      cell.appendChild(span);
    }
    span.textContent = formatLastSeen(now);
  }

  // === STATUS UPDATE + PULSE ===
  function updateStatus(cell, isActive, value = null, regName = null) {
    cell.classList.remove("bg-success", "bg-danger", "bg-warning");
    cell.classList.add(isActive ? "bg-success" : "bg-danger");

    // Value
    if (value !== null && value !== undefined) {
      let valSpan = cell.querySelector(".value");
      if (!valSpan) {
        valSpan = document.createElement("span");
        valSpan.className = "value ml-2 text-sm font-medium";
        cell.appendChild(valSpan);
      }
      valSpan.textContent = `${value.toFixed(1)}`;
    }

    // Last seen
    if (regName) updateLastSeen(cell, regName);

    // Pulse
    cell.animate([
      { transform: "scale(1)", opacity: 1 },
      { transform: "scale(1.12)", opacity: 0.85 },
      { transform: "scale(1)", opacity: 1 }
    ], { duration: 500, easing: "ease-out" });
  }

  // === SUBSCRIBE TO ALL UNITS ===
  console.log("[CommunicationPage] Subscribing to registers");

  Object.entries(configKeys).forEach(([type, configKey]) => {
    const group = groups[configKey] || [];
    if (!group.length) {
      console.warn(`[CommunicationPage] No registers for ${configKey}`);
      return;
    }
    const table = typeToTable[type];
    if (!table) return;

    group.forEach(reg => {
      const match = reg.name.match(/_(\d+)$/);
      const suffix = match ? match[1] : "";
      let expectedLabel = "";

      if (type === "inverter") expectedLabel = `INV ${suffix.padStart(2, "0")}`;
      else if (type === "mv") expectedLabel = "MVT";
      else if (type === "generator") expectedLabel = `Generator Line ${suffix || 1}`;
      else if (type === "solar") expectedLabel = `Solar Line ${suffix || 1}`;
      else if (type === "bess") expectedLabel = `BESS Line ${suffix || 1}`;

      const row = Array.from(table.querySelectorAll("tbody tr")).find(r => {
        const label = r.querySelector("td:first-child")?.textContent.trim();
        return label === expectedLabel;
      });

      const statusCell = row?.querySelector("td:nth-child(3)");
      if (!statusCell) return;

      const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, payload => {
        const raw = payload?.value ?? null;
        const value = typeof raw === 'number' ? raw : null;

        const isActive = type === "bess" ? (value ?? 0) > 10 : (value ?? 0) > 0.1;
        updateStatus(statusCell, isActive, value, reg.name);
      });

      subscriptions.push(unsub);
    });
  });

  // === INIT & CLEANUP ===
  requestAnimationFrame(() => {
    console.log("[CommunicationPage] Initializing fullscreen toggle");
    new FullscreenToggle().init();
  });

  container.cleanup = () => {
    console.log(`[CommunicationPage] Cleaning up ${subscriptions.length} subscriptions`);
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
  };

  console.log("[CommunicationPage] Initialization complete");
  return container;
}